import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, RefreshCw, Send, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';

const SMTPSettings = () => {
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 465,
    smtp_username: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_to_email: '',
    smtp_use_tls: true
  });

  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        console.error('No admin token found');
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
    } catch (error) {
      console.error('Error loading SMTP settings:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveMessage(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/settings`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSaveMessage({
        type: 'success',
        message: response.data.message
      });
    } catch (error) {
      setSaveMessage({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to save SMTP settings'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/test-connection`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Connection test failed',
        details: {}
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter an email address for the test');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/smtp/send-test-email?test_email=${testEmail}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult(response.data);
    } catch (error) {
      setTestResult({
        success: false,
        message: 'Failed to send test email'
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* SMTP Configuration Card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center mb-6">
            <Mail className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">SMTP Configuration</h2>
          </div>

          {saveMessage && (
            <div className={`mb-6 p-4 rounded-lg ${
              saveMessage.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {saveMessage.message}
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_host">SMTP Host</Label>
                <Input
                  id="smtp_host"
                  name="smtp_host"
                  value={settings.smtp_host}
                  onChange={handleInputChange}
                  placeholder="mail.example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_port">SMTP Port</Label>
                <Input
                  id="smtp_port"
                  name="smtp_port"
                  type="number"
                  value={settings.smtp_port}
                  onChange={handleInputChange}
                  placeholder="465"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_username">SMTP Username</Label>
                <Input
                  id="smtp_username"
                  name="smtp_username"
                  value={settings.smtp_username}
                  onChange={handleInputChange}
                  placeholder="smtp@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_password">SMTP Password</Label>
                <Input
                  id="smtp_password"
                  name="smtp_password"
                  type="password"
                  value={settings.smtp_password}
                  onChange={handleInputChange}
                  placeholder="Enter password"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="smtp_from_email">From Email</Label>
                <Input
                  id="smtp_from_email"
                  name="smtp_from_email"
                  type="email"
                  value={settings.smtp_from_email}
                  onChange={handleInputChange}
                  placeholder="noreply@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="smtp_to_email">Notification Email</Label>
                <Input
                  id="smtp_to_email"
                  name="smtp_to_email"
                  type="email"
                  value={settings.smtp_to_email}
                  onChange={handleInputChange}
                  placeholder="admin@example.com"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smtp_use_tls"
                name="smtp_use_tls"
                checked={settings.smtp_use_tls}
                onChange={handleInputChange}
                className="rounded"
              />
              <Label htmlFor="smtp_use_tls" className="cursor-pointer">
                Use TLS/SSL (recommended for port 465)
              </Label>
            </div>

            <div className="pt-4">
              <Button 
                onClick={handleSave} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? 'Saving...' : 'Save SMTP Settings'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test SMTP Card */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Test SMTP Configuration</h3>

          {testResult && (
            <div className={`mb-4 p-4 rounded-lg flex items-start ${
              testResult.success 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              {testResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              )}
              <div className="flex-1">
                <p className={testResult.success ? 'text-green-800' : 'text-red-800'}>
                  {testResult.message}
                </p>
                {testResult.details && (
                  <div className="mt-2 text-sm text-gray-600">
                    <p>Host: {testResult.details.host}</p>
                    <p>Port: {testResult.details.port}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex gap-2">
              <Button 
                onClick={handleTestConnection} 
                disabled={testing}
                variant="outline"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
                {testing ? 'Testing...' : 'Test Connection'}
              </Button>
            </div>

            <div className="border-t pt-4">
              <Label htmlFor="testEmail">Send Test Email To:</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  id="testEmail"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder={settings.smtp_to_email || "Enter email address"}
                  className="flex-1"
                />
                <Button 
                  onClick={handleSendTestEmail} 
                  disabled={testing || !testEmail}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Send a test email to verify your SMTP configuration is working correctly
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SMTPSettings;
